import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GraduationCapIcon, ShieldCheckIcon, MailIcon, ArrowRightIcon, CheckIcon, LockIcon, ZapIcon, ClipboardListIcon, SparklesIcon, SchoolIcon, StarIcon, AwardIcon } from 'lucide-react';
import { getBridgeTemplate } from '../data/bridgeTemplates';
import { useMetaTags } from '../hooks/useMetaTags';
import { getPublisherParams } from '../utils/publisherParams';
import { degreeLevelOptions, areaStudyOptions, programOptions, getDefaultProgram } from '../data/filterOptions';
const NAVY = '#1a2332';
const NAVY_ACCENT = '#2a374a';
const ORANGE = '#e8594f';
const ORANGE_HOVER = '#d64a41';
export function FindDegreesBridgePage() {
  const {
    templateId
  } = useParams<{
    templateId: string;
  }>();
  const template = getBridgeTemplate(templateId);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // v6 selector state
  const [degreeLevel, setDegreeLevel] = useState<number | ''>('');
  const [areaOfStudy, setAreaOfStudy] = useState<number | ''>('');
  const [program, setProgram] = useState<number | ''>('');
  // v8 — Clicks Search carousel state. When `showClicksCarousel` is
  // true, the page swaps the selector form for an in-page carousel
  // that displays one school at a time from the Clicks API response.
  const [showClicksCarousel, setShowClicksCarousel] = useState(false);
  const [clicksResults, setClicksResults] = useState<any[]>([]);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [clicksIndex, setClicksIndex] = useState(0);
  const [clicksError, setClicksError] = useState('');
  useMetaTags({
    title: `${template.headlineHighlight} | FindOnlineDegrees`,
    description: template.subhead,
    // Bridge templates are paid traffic landing pages — keep them out of
    // the organic index so they don't compete with content pages.
    robots: 'noindex, follow'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('email', email);
    navigate(`/?${params.toString()}`);
  };
  const handleCtaClick = () => {
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    // healthcare-v1: same single-button CTA layout as v2, but on click
    // swap the page for the in-page Clicks carousel with degreeInterest=5
    // (Master's) and mProgram=500 (Healthcare) hardcoded.
    if (templateId === 'healthcare-v1') {
      setDegreeLevel(5);
      setProgram(500);
      setShowClicksCarousel(true);
      callV8ClicksAPI(5, 500);
      return;
    }
    // v2: open the Media Matchers API as a pop-under and keep the user on
    // the listings page in the foreground tab.
    if (templateId === 'v2') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        mediaType: '2',
        redirect: '1'
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    // v5: navigate to the colleges page (preserve any sub params)
    if (templateId === 'v5') {
      navigate(`/colleges?${params.toString()}`);
      return;
    }
    // v3 only: redirect to findonlinedegrees.org request-information page
    if (templateId === 'v3') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || ''
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      window.location.href = `https://findonlinedegrees.org/request-information?${externalParams.toString()}`;
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  const isCta = template.variant === 'cta';
  const isSelector = template.variant === 'selector';
  const availablePrograms = areaOfStudy ? programOptions[areaOfStudy as number] || [] : [];
  // Fetch Clicks Search API and render results inline as carousel.
  // Used by both v8 (selector form) and healthcare-v1 (single CTA).
  // Accepts explicit overrides because state setters are async — the
  // caller may have just set state in the same tick.
  const callV8ClicksAPI = async (degreeInterestOverride?: number | '', mProgramOverride?: number | '') => {
    setClicksLoading(true);
    setClicksError('');
    setClicksResults([]);
    const di = degreeInterestOverride !== undefined ? degreeInterestOverride : degreeLevel;
    const mp = mProgramOverride !== undefined ? mProgramOverride : program;
    const params = new URLSearchParams(location.search);
    const searchParams = new URLSearchParams({
      degreeInterest: di === '' ? '' : String(di),
      mProgram: mp === '' ? '' : String(mp),
      sub1: params.get('sub1') || '',
      sub2: params.get('sub2') || '',
      sub3: params.get('sub3') || ''
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    const url = `https://api.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[v8 Clicks API] PING (GET):', url);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[v8 Clicks API] response:', data);
      const schools = data?.result || data?.results || data?.schools || data?.data || (Array.isArray(data) ? data : []) || [];
      setClicksResults(Array.isArray(schools) ? schools : []);
      if (!schools || schools.length === 0) {
        setClicksError('No matching schools found.');
      }
    } catch (err) {
      clearTimeout(timeout);
      console.error('[v8 Clicks API] error:', err);
      setClicksError('Unable to load matching schools. Please try again.');
    } finally {
      setClicksLoading(false);
    }
  };
  const handleV8ClickThrough = () => {
    const school: any = clicksResults[clicksIndex];
    const clickURL = school?.clickURL || school?.clickUrl || school?.url || school?.link;
    if (clickURL) {
      try {
        window.open(clickURL, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.warn('[v8 Clicks] failed to open clickURL:', err);
      }
    }
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams(location.search);
      params.set('degreeInterest', String(degreeLevel));
      params.set('mProgram', String(program));
      navigate(`/?${params.toString()}`);
    }
  };
  const handleV8Skip = () => {
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams(location.search);
      params.set('degreeInterest', String(degreeLevel));
      params.set('mProgram', String(program));
      navigate(`/?${params.toString()}`);
    }
  };
  const handleSelectorSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!degreeLevel || !areaOfStudy || !program) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('degreeInterest', String(degreeLevel));
    params.set('mProgram', String(program));
    // v8 and financial-aid-v1: swap to in-page Clicks API carousel.
    if (templateId === 'v8' || templateId === 'financial-aid-v1') {
      setShowClicksCarousel(true);
      callV8ClicksAPI();
      return;
    }
    // v7: redirect to external Media Matchers search API,
    // and also open the home page (with the same selections) as a pop-under.
    if (templateId === 'v7') {
      const externalParams = new URLSearchParams({
        degreeInterest: String(degreeLevel),
        mProgram: String(program),
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        redirect: '1'
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      // Pop-under: open the API URL in a new tab and drop it behind,
      // then navigate the current window to the home/listings page so the
      // user stays on our site.
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  // v8: when the carousel is active, render it in place of the
  // selector page. Keeps the slim header for brand consistency.
  if (showClicksCarousel) {
    const currentClick: any = clicksResults[clicksIndex];
    const cSchoolName = currentClick?.mBrandName || currentClick?.brandName || currentClick?.name || 'Partner School';
    const cSchoolLogo = currentClick?.imageURL || currentClick?.imageUrl || currentClick?.logoUrl;
    const cHeadline = currentClick?.headContent || '';
    const cSchoolHighlight = currentClick?.schoolHighlight || '';
    const cAboutText = currentClick?.aboutBrand || currentClick?.aboutCollege || currentClick?.shortDescription || '';
    const cAccreditation = currentClick?.accreditation || '';
    const cRichHtml = currentClick?.bodyContent || '';
    const cClickURL = currentClick?.clickURL || currentClick?.clickUrl || currentClick?.url || currentClick?.link;
    return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-50" data-id="element-946">
        <header className="w-full relative overflow-hidden" style={{
        backgroundColor: NAVY
      }} data-id="element-947">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-948"></div>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-949">
            <div className="flex items-center gap-2.5" data-id="element-950">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
              backgroundColor: ORANGE
            }} data-id="element-951">
                <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-952" />
              </div>
              <div className="leading-tight" data-id="element-953">
                <div className="font-bold text-white text-base" data-id="element-954">
                  FindOnlineDegrees
                </div>
                <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-955">
                  YOUR MATCHES
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-956">
              <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-957" />
              <span data-id="element-958">SSL Secured</span>
            </div>
          </div>
        </header>
        <main className="flex-grow" data-id="element-959">
          <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-960">
            {clicksLoading ? <div className="text-center py-20" data-id="element-961">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-962"></div>
                <p className="text-xl text-gray-700 font-semibold" data-id="element-963">
                  Finding your matches...
                </p>
              </div> : clicksError || clicksResults.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-gray-200 shadow-sm" data-id="element-964">
                <p className="text-xl text-gray-700 font-semibold mb-4" data-id="element-965">
                  {clicksError || 'No matches found.'}
                </p>
                <button onClick={() => {
              const params = new URLSearchParams(location.search);
              params.set('degreeInterest', String(degreeLevel));
              params.set('mProgram', String(program));
              navigate(`/?${params.toString()}`);
            }} className="px-6 py-3 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg transition-all" data-id="element-966">
                  Continue to Listings →
                </button>
              </div> : <>
                <div className="text-center mb-8" data-id="element-967">
                  <p className="text-xs uppercase tracking-widest text-[#e8594f] font-bold mb-1" data-id="element-968">
                    Your Matches
                  </p>
                  <p className="text-sm text-gray-600 font-semibold" data-id="element-969">
                    School {clicksIndex + 1} of {clicksResults.length}
                  </p>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-970">
                    <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
                  width: `${(clicksIndex + 1) / clicksResults.length * 100}%`
                }} data-id="element-971" />
                  </div>
                </div>
                <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-972">
                  {cSchoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-973">
                      <span aria-hidden="true" data-id="element-974">✨</span>
                      {cSchoolHighlight}
                    </span>}
                  <div className="text-center mb-8" data-id="element-975">
                    {cSchoolLogo && <img src={cSchoolLogo} alt={cSchoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-976" />}
                    {cAboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-977">
                        {cAboutText}
                      </p>}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2" data-id="element-978">
                    {cSchoolName}
                  </h2>
                  {cHeadline && cHeadline.trim() !== cSchoolName.trim() && <p className="text-base md:text-lg text-[#e8594f] font-semibold text-center mb-3" data-id="element-979">
                      {cHeadline}
                    </p>}
                  {cAccreditation && <p className="text-xs text-gray-500 text-center mb-5" data-id="element-980">
                      Accredited by {cAccreditation}
                    </p>}
                  {cRichHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
                __html: cRichHtml
              }} data-id="element-981" />}
                  <div className="space-y-4" data-id="element-982">
                    <button onClick={handleV8ClickThrough} disabled={!cClickURL} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-983">
                      Visit {cSchoolName} →
                    </button>
                    <button onClick={handleV8Skip} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all" data-id="element-984">
                      Skip This School
                    </button>
                  </div>
                </div>
              </>}
          </div>
        </main>
        {template.footerDisclosure && <footer className="w-full border-t border-gray-200 bg-white mt-auto" data-id="element-985">
            <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-500" data-id="element-986">
              <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4 font-medium" data-id="element-987">
                <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-988">
                  CA Consumers: Do Not Sell My Info
                </a>
                <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-989">
                  Terms
                </a>
                <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-990">
                  Privacy Policy
                </a>
              </nav>
              <p className="text-xs mb-4" data-id="element-991">
                © {new Date().getFullYear()} FindOnlineDegrees. All rights
                reserved.
              </p>
              <p className="text-[10px] leading-relaxed max-w-5xl mx-auto text-justify" data-id="element-992">
                {template.footerDisclosure}
              </p>
            </div>
          </footer>}
      </div>;
  }
  return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-100" data-id="element-993">
      {/* Slim Header — matches site nav style. Hidden on mobile to maximize
                                above-the-fold form visibility. */}
      <header className="w-full relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-994">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-995"></div>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-996">
          <div className="flex items-center gap-2.5" data-id="element-997">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
            backgroundColor: ORANGE
          }} data-id="element-998">
              <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-999" />
            </div>
            <div className="leading-tight" data-id="element-1000">
              <div className="font-bold text-white text-base" data-id="element-1001">
                FindOnlineDegrees
              </div>
              <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-1002">
                FREE DEGREE MATCHING
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-1003">
            <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-1004" />
            <span data-id="element-1005">SSL Secured</span>
          </div>
        </div>
      </header>

      {/* Main Content — Two-Column, vertically centered */}
      <section className="flex-grow flex items-center" data-id="element-1006">
        <div className="w-full max-w-7xl mx-auto px-4 py-3 md:py-16 lg:py-20" data-id="element-1007">
          {/* Mobile-only eyebrow + H1 — sits ABOVE the form so the user
           reads the value prop, then immediately sees the form. */}
          <div className="lg:hidden text-center mb-5" data-id="element-1008">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: '#4A49E5',
            color: '#ffffff'
          }} data-id="element-1009">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: '#ffffff'
            }} data-id="element-1010"></span>
              {template.eyebrow}
            </div>
            <h1 className="font-bold leading-[1.1] text-gray-900" style={{
            fontSize: 'clamp(1.75rem, 7vw, 2.5rem)'
          }} data-id="element-1011">
              {template.headlineLead}{' '}
              <span style={{
              color: ORANGE
            }} data-id="element-1012">
                {template.headlineHighlight}
              </span>{' '}
              {template.headlineTrail}
            </h1>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-10 lg:gap-16 items-center" data-id="element-1013">
            {/* LEFT: Headline — appears BELOW the form on mobile, beside it on desktop */}
            <div className="text-center lg:text-left order-2 lg:order-1" data-id="element-1014">
              {/* Desktop-only eyebrow + H1 (mobile renders these above the grid) */}
              <div className="hidden lg:block" data-id="element-1015">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-5 text-[11px] font-bold tracking-widest" style={{
                backgroundColor: '#4A49E5',
                color: '#ffffff'
              }} data-id="element-1016">
                  <span className="w-1.5 h-1.5 rounded-full" style={{
                  backgroundColor: '#ffffff'
                }} data-id="element-1017"></span>
                  {template.eyebrow}
                </div>

                <h1 className="font-bold leading-[1.1] mb-5 text-gray-900" style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.25rem)'
              }} data-id="element-1018">
                  {template.headlineLead}{' '}
                  <span style={{
                  color: ORANGE
                }} data-id="element-1019">
                    {template.headlineHighlight}
                  </span>{' '}
                  {template.headlineTrail}
                </h1>
              </div>

              {template.financialAid ? <div className="space-y-6 max-w-xl mx-auto lg:mx-0 text-left" data-id="element-1020">
                  <div className="bg-white rounded-xl p-6 border-l-4 shadow-sm" style={{
                borderColor: ORANGE
              }} data-id="element-1021">
                    <p className="text-gray-600 font-medium mb-1" data-id="element-1022">
                      {template.financialAid.grantCaption.split(template.financialAid.grantAmount)[0]}
                    </p>
                    <div className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-1" data-id="element-1023">
                      {template.financialAid.grantAmount}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-1024">
                      {template.financialAid.grantCaption.split(template.financialAid.grantAmount)[1]}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-id="element-1025">
                    <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-2" data-id="element-1026">
                      {template.financialAid.earningsStat}
                    </p>
                    <p className="text-xs text-gray-400 leading-tight" data-id="element-1027">
                      {template.financialAid.earningsDisclaimer}
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1028">
                    <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-1029">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-1030">
                        {template.financialAid.weCanHeadline}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1031">
                        {template.financialAid.weCanBody}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-1032">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-1033">
                        {template.financialAid.exploreHeadline}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1034">
                        {template.financialAid.exploreBody}
                      </p>
                    </div>
                  </div>
                </div> : <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-1035">
                  {template.subhead}
                </p>}

              {/* Stats strip — site theme */}
            </div>

            {/* RIGHT: Form — appears ABOVE the supporting text on mobile */}
            <aside className="w-full max-w-md mx-auto lg:max-w-none lg:mx-0 order-1 lg:order-2" data-id="element-1036">
              {/* Step indicator */}
              <div className="flex items-center gap-3 mb-5 px-1" data-id="element-1037">
                <div className="flex items-center gap-2" data-id="element-1038">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{
                  backgroundColor: ORANGE
                }} data-id="element-1039">
                    <CheckIcon className="w-4 h-4" data-id="element-1040" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700" data-id="element-1041">
                    Profile Complete
                  </span>
                </div>
                <div className="flex-grow h-px bg-gray-300" data-id="element-1042"></div>
                <div className="flex items-center gap-2" data-id="element-1043">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse" style={{
                  backgroundColor: NAVY,
                  boxShadow: `0 0 0 4px rgba(232,89,79,0.15)`
                }} data-id="element-1044">
                    2
                  </div>
                  <span className="text-xs font-semibold" style={{
                  color: NAVY
                }} data-id="element-1045">
                    Confirm Email
                  </span>
                </div>
              </div>

              {isSelector ? <form onSubmit={handleSelectorSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1046">
                  <div className="text-center mb-6" data-id="element-1047">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-1048">
                      {template.cardTitle}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-1049">
                      {template.cardSubtitle}
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-1050">
                    <div data-id="element-1051">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1052">
                        Degree Level <span className="text-red-500" data-id="element-1053">*</span>
                      </label>
                      <select value={degreeLevel} onChange={e => setDegreeLevel(e.target.value ? Number(e.target.value) : '')} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1054">
                        <option value="" data-id="element-1055">-- Please Select --</option>
                        {degreeLevelOptions.filter(o => o.value !== 5).map(option => <option key={option.value} value={option.value} data-id="element-1056">
                              {option.label}
                            </option>)}
                      </select>
                    </div>

                    <div data-id="element-1057">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1058">
                        Area of Study <span className="text-red-500" data-id="element-1059">*</span>
                      </label>
                      <select value={areaOfStudy} onChange={e => {
                    const v = e.target.value ? Number(e.target.value) : '';
                    setAreaOfStudy(v);
                    setProgram('');
                  }} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1060">
                        <option value="" data-id="element-1061">-- Please Select --</option>
                        {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-1062">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <div data-id="element-1063">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1064">
                        Program <span className="text-red-500" data-id="element-1065">*</span>
                      </label>
                      <select value={program} onChange={e => setProgram(e.target.value ? Number(e.target.value) : '')} required disabled={!areaOfStudy} className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1066">
                        <option value="" data-id="element-1067">
                          {areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                        </option>
                        {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-1068">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <button type="submit" disabled={submitting || !degreeLevel || !areaOfStudy || !program} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed" style={{
                  backgroundColor: ORANGE
                }} onMouseEnter={e => {
                  if (!(e.currentTarget as HTMLButtonElement).disabled) {
                    ;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
                  }
                }} onMouseLeave={e => {
                  ;
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
                }} data-id="element-1069">
                      <span data-id="element-1070">{submitting ? 'Loading…' : template.ctaLabel}</span>
                      <ArrowRightIcon className="w-4 h-4" data-id="element-1071" />
                    </button>

                    <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5" data-id="element-1072">
                      <LockIcon className="w-3.5 h-3.5" data-id="element-1073" />
                      Your information is secure and confidential
                    </p>
                  </div>
                </form> : isCta ? <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-gray-200 text-center" data-id="element-1074">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1075">
                    {template.cardHeadline || template.cardTitle}
                  </h2>
                  <p className="text-base font-semibold mb-7" style={{
                color: ORANGE
              }} data-id="element-1076">
                    {template.cardSubcopy || template.cardSubtitle}
                  </p>

                  <button type="button" onClick={handleCtaClick} disabled={submitting} className="inline-flex items-center justify-center gap-2 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1077">
                    <span data-id="element-1078">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1079" />
                  </button>

                  {template.cardFootnote && <p className="text-sm text-gray-600 mt-7 leading-relaxed max-w-sm mx-auto" data-id="element-1080">
                      {template.cardFootnote}
                    </p>}
                </div> : <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1081">
                  <label htmlFor="bridge-email" className="block text-sm font-bold mb-3 text-gray-900" data-id="element-1082">
                    Where should we send your matches?
                  </label>

                  <div className="relative mb-4" data-id="element-1083">
                    <MailIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1084" />
                    <input id="bridge-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="yourname@example.com" className="w-full pl-12 pr-4 py-3.5 rounded-lg border-2 border-gray-200 bg-white text-base text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors" data-id="element-1085" />
                  </div>

                  <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1086">
                    <span data-id="element-1087">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1088" />
                  </button>

                  {/* Inline trust row */}
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5 text-xs text-gray-600" data-id="element-1089">
                    <div className="flex items-center gap-1.5" data-id="element-1090">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1091" />
                      <span data-id="element-1092">No credit card</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1093">
                      <ZapIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1094" />
                      <span data-id="element-1095">Instant results</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1096">
                      <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1097" />
                      <span data-id="element-1098">100% free</span>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 mt-5 leading-relaxed text-center" data-id="element-1099">
                    By clicking, you agree to our{' '}
                    <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-1100">
                      Terms
                    </a>{' '}
                    &{' '}
                    <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-1101">
                      Privacy Policy
                    </a>{' '}
                    and consent to receive program info via email, phone, or
                    text.
                  </p>
                </form>}
            </aside>
          </div>
        </div>
      </section>

      {/* Content Section — How it works + Testimonials + Trust */}
      <section className="w-full bg-white border-t border-gray-200" data-id="element-1102">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-20" data-id="element-1103">
          {/* How It Works */}
          <div className="text-center mb-12" data-id="element-1104">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: 'rgba(232,89,79,0.1)',
            color: ORANGE
          }} data-id="element-1105">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: ORANGE
            }} data-id="element-1106"></span>
              HOW IT WORKS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1107">
              Your Path to the Right Program in 3 Steps
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1108">
              We've helped thousands of students find accredited online programs
              that fit their goals, schedule, and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-1109">
            {[{
            icon: ClipboardListIcon,
            step: '01',
            title: 'Tell Us About You',
            body: 'Share your goals, education level, and area of interest in under a minute.'
          }, {
            icon: SparklesIcon,
            step: '02',
            title: 'Get Matched Instantly',
            body: 'Our system pairs you with accredited online programs that fit your profile.'
          }, {
            icon: SchoolIcon,
            step: '03',
            title: 'Connect With Schools',
            body: 'Compare programs side-by-side and connect directly with admissions teams.'
          }].map((s, i) => {
            const Icon = s.icon;
            return <div key={i} className="relative bg-gray-50 rounded-2xl p-7 border border-gray-200 hover:border-[#e8594f]/40 hover:shadow-md transition-all" data-id="element-1110">
                  <div className="flex items-center justify-between mb-5" data-id="element-1111">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                  backgroundColor: 'rgba(232,89,79,0.1)'
                }} data-id="element-1112">
                      <Icon className="w-6 h-6" style={{
                    color: ORANGE
                  }} data-id="element-1113" />
                    </div>
                    <span className="text-3xl font-bold text-gray-200" data-id="element-1114">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1115">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1116">
                    {s.body}
                  </p>
                </div>;
          })}
          </div>

          {/* Testimonials */}
          {template.testimonials && template.testimonials.length > 0 && <div className="border-t border-gray-200 pt-16" data-id="element-1117">
              <div className="text-center mb-10" data-id="element-1118">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1119">
                  Trusted by Students Like You
                </h2>
                <div className="flex items-center justify-center gap-1.5 text-gray-600 text-sm" data-id="element-1120">
                  {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-4 h-4 fill-current" style={{
                color: ORANGE
              }} data-id="element-1121" />)}
                  <span className="ml-2" data-id="element-1122">
                    4.8 average rating from 70,000+ matched students
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-1123">
                {template.testimonials.map((t, i) => <div key={i} className="bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-200" data-id="element-1124">
                    <div className="flex gap-1 mb-3" data-id="element-1125">
                      {[1, 2, 3, 4, 5].map(s => <StarIcon key={s} className="w-4 h-4 fill-current" style={{
                  color: ORANGE
                }} data-id="element-1126" />)}
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-5 italic" data-id="element-1127">
                      "{t.quote}"
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-gray-200" data-id="element-1128">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{
                  backgroundColor: NAVY
                }} data-id="element-1129">
                        {t.initial}
                      </div>
                      <div data-id="element-1130">
                        <div className="font-semibold text-gray-900 text-sm" data-id="element-1131">
                          {t.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1132">{t.school}</div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* Trust band */}
          <div className="mt-16 rounded-2xl p-8 md:p-10 relative overflow-hidden" style={{
          backgroundColor: NAVY
        }} data-id="element-1133">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1134"></div>
            <div className="relative z-10 grid md:grid-cols-3 gap-6 items-center" data-id="element-1135">
              <div className="flex items-start gap-3" data-id="element-1136">
                <AwardIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1137" />
                <div data-id="element-1138">
                  <div className="font-bold text-white mb-1" data-id="element-1139">
                    Only Accredited Schools
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1140">
                    Every partner school is regionally or nationally accredited.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1141">
                <ShieldCheckIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1142" />
                <div data-id="element-1143">
                  <div className="font-bold text-white mb-1" data-id="element-1144">
                    Your Privacy Protected
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1145">
                    SSL encrypted. We never sell your data to unrelated third
                    parties.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1146">
                <ZapIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1147" />
                <div data-id="element-1148">
                  <div className="font-bold text-white mb-1" data-id="element-1149">
                    Always 100% Free
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1150">
                    No credit card, no fees — for you, ever.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full border-t border-gray-200 bg-white" data-id="element-1151">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-700" data-id="element-1152">
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4" data-id="element-1153">
            <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1154">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1155">
              Terms of Use
            </a>
            <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1156">
              CA Privacy Rights
            </a>
            <a href="/cookie-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1157">
              Cookies
            </a>
          </nav>
          <p className="text-xs text-gray-600 mb-1" data-id="element-1158">
            © {new Date().getFullYear()} FindOnlineDegrees.org · Media Matchers
            LLC · All Rights Reserved.
          </p>
          <p className="text-[11px] text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1159">
            FindOnlineDegrees.org is a free service that helps students explore
            accredited online degree programs. We may receive compensation from
            school partners when users inquire.
          </p>
        </div>
      </footer>
    </div>;
}